import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
} from "@angular/core";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { fromEvent } from "rxjs";

import { provideIcons } from "@ng-icons/core";
import { heroChatBubbleLeft } from "@ng-icons/heroicons/outline";

import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";
import { EVA_OBJECT_TAGS, TagPreset } from "src/app/shared/constants/tag-presets";
import { Task, TaskAssignee, TaskObjectLink, TaskStatus, TaskUrgency, getAssigneeName } from "../domains/task";
import { TaskCategory as TaskCategoryModel } from "../domains/task-category";

const OBJECT_TYPE_TO_EVA: Record<string, string> = {
  CUSTOMER: "CLIENT",
  PROJECT: "CHANTIER",
  OFFER: "OFFRE",
  INVOICE: "FACTURE_CLIENT",
  EMPLOYEE: "COLLABORATEUR",
  SUPPLIER: "FOURNISSEUR",
  PURCHASE_ORDER: "BON_COMMANDE",
  SUPPLIER_INVOICE: "FACTURE_ACHAT",
  INTERVENTION: "INTERVENTION",
  CONTRACT: "CONTRAT",
  CUSTOMER_SITE: "SITE",
  CATALOG: "CATALOGUE",
  ARTICLE: "ARTICLE",
  EXPENSE_REPORT: "NOTE_FRAIS",
  CONTACT: "CONTACT",
};

/** Routes de détail par type d'objet lié (mêmes URL que NoteContextService) */
const OBJECT_LINK_ROUTES: Record<string, string> = {
  OFFER: "/offers",
  PROJECT: "/projects",
  CUSTOMER: "/customers",
  INVOICE: "/invoices/issued",
  INTERVENTION: "/interventions",
  SUPPLIER: "/suppliers",
  PURCHASE_ORDER: "/purchase-orders",
  EMPLOYEE: "/employees",
  CONTACT: "/contacts",
  ARTICLE: "/catalog/articles",
};

@Component({
  selector: "app-task-row",
  templateUrl: "./task-row.component.html",
  styleUrls: ["./task-row.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideIcons({ heroChatBubbleLeft })],
})
export class TaskRowComponent {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private host = inject(ElementRef<HTMLElement>);

  /** Clic à l'extérieur, en capture : le galet arrête la propagation des clics */
  private readonly stopOutsideClick = onDocumentClickCapture(
    () => this.showRowMenu,
    (event) => this.onDocumentClick(event),
  );

  constructor() {
    fromEvent<KeyboardEvent>(document, "keydown")
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (event.key === "Escape" && this.showRowMenu) {
          this.showRowMenu = false;
          this.cdr.markForCheck();
        }
      });
  }

  showRowMenu = false;
  menuPosition = { top: 0, bottom: 0, right: 0 };
  menuOpensUpward = false;
  /** Masque le menu le temps de corriger sa position (voir placeMenuUnder) */
  menuPlaced = false;
  private _ignoreNextDocumentClick = false;

  // ==========================================================================
  // Inputs
  // ==========================================================================

  @Input({ required: true }) task: Task;
  @Input() employees: any[] = [];
  @Input() categories: TaskCategoryModel[] = [];
  @Input() compact = false;
  @Input() showObjectLink = true;
  @Input() showDragHandle = false;
  @Input() showDate = true;
  @Input() showAssignees = true;
  @Input() showProgress = false;

  // ==========================================================================
  // Outputs
  // ==========================================================================

  @Input() selected = false;
  @Input() selectionMode = false;

  @Output() statusChange = new EventEmitter<{ task: Task; done: boolean; status: TaskStatus }>();
  @Output() taskClick = new EventEmitter<Task>();
  @Output() menuAction = new EventEmitter<{ task: Task; action: string }>();
  @Output() detailRequested = new EventEmitter<Task>();
  @Output() selectionToggle = new EventEmitter<Task>();
  @Output() contextMenuRequested = new EventEmitter<{ task: Task; x: number; y: number; alignRight?: boolean }>();

  // ==========================================================================
  // Helpers — assignees
  // ==========================================================================

  getAssigneeInitials(assignee: TaskAssignee): string {
    if (assignee.type === "external") {
      return (assignee.name || "??").substring(0, 2).toUpperCase();
    }
    const emp = this.employees.find((e) => e.employee_id === assignee.employee_id);
    if (!emp) return "??";
    if (emp.initials?.trim()) return emp.initials.trim().substring(0, 2).toUpperCase();
    return `${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}`.toUpperCase() || "??";
  }

  getAssigneeColor(assignee: TaskAssignee): string {
    if (assignee.type === "external") return "#BEBEBE";
    const emp = this.employees.find((e) => e.employee_id === assignee.employee_id);
    return emp?.badge_color || "#BEBEBE";
  }

  /** Nom affichable du collaborateur pour la tooltip (résolu depuis employees). */
  getAssigneeTooltip(assignee: TaskAssignee): string {
    // getAssigneeName retombe sur l'id brut : hors de la liste, on affiche un libellé lisible
    const known = assignee.type === "external" || this.employees.some((e) => e.employee_id === assignee.employee_id);
    return (known && getAssigneeName(assignee, this.employees)) || "Collaborateur introuvable";
  }

  // ==========================================================================
  // Helpers — categories
  // ==========================================================================

  getCategoryById(id: string): TaskCategoryModel | null {
    return this.categories.find((c) => c.category_id === id) || null;
  }

  getTaskCategories(): TaskCategoryModel[] {
    if (!this.task?.category_ids?.length) return [];
    return this.task.category_ids.map((id) => this.getCategoryById(id)).filter((c): c is TaskCategoryModel => !!c);
  }

  // ==========================================================================
  // Helpers — dates
  // ==========================================================================

  /** Déduit le mode de date de la tâche */
  getDateMode(): "none" | "deadline" | "range" {
    if (this.task?.planned_start_date != null && this.task?.due_date != null) return "range";
    if (this.task?.due_date != null) return "deadline";
    return "none";
  }

  isToday(date: Date): boolean {
    if (!date) return false;
    const today = this.getStartOfDay(new Date());
    const due = this.getStartOfDay(date);
    return due.getTime() === today.getTime();
  }

  formatDueDate(date: Date): string {
    if (!date) return "";

    const today = this.getStartOfDay(new Date());
    const dueDay = this.getStartOfDay(date);
    const diff = Math.round((dueDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));

    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return "Demain";
    if (diff < 0) {
      const days = Math.abs(diff);
      return `${days} j de retard`;
    }

    return date.toLocaleDateString("fr-CH", { day: "numeric", month: "short" });
  }

  /** Formate une date courte (ex: 15.03) */
  formatShortDate(date: Date): string {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}.${month}`;
  }

  /** Label d'affichage pour le mode période */
  formatRangeDate(): string {
    const start = this.task?.planned_start_date;
    const end = this.task?.due_date;
    if (!start && !end) return "";
    if (!start) return this.formatShortDate(end);
    if (!end) return this.formatShortDate(start);
    return `${this.formatShortDate(start)} → ${this.formatShortDate(end)}`;
  }

  isOverdue(): boolean {
    return this.task?.isOverdue() || false;
  }

  isDone(): boolean {
    return this.task?.status === TaskStatus.DONE || this.task?.status === TaskStatus.CANCELLED;
  }

  isInProgress(): boolean {
    return this.task?.status === TaskStatus.IN_PROGRESS;
  }

  getCheckboxAriaLabel(): string {
    return this.isDone() ? "Marquer comme à faire" : "Marquer comme terminée";
  }

  isUrgent(): boolean {
    return this.task?.urgency === TaskUrgency.URGENT;
  }

  isLowPriority(): boolean {
    return this.task?.urgency === TaskUrgency.LOW;
  }

  getUrgencyLabel(): string {
    if (this.task?.urgency === TaskUrgency.URGENT) return "Urgent";
    if (this.task?.urgency === TaskUrgency.LOW) return "Basse";
    return "";
  }

  private getStartOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  // ==========================================================================
  // Helpers — object link
  // ==========================================================================

  getFirstObjectLink() {
    return this.task?.objects_links?.[0] || null;
  }

  getObjectLinks(): TaskObjectLink[] {
    return (this.task?.objects_links || []).filter((l) => !!l.object_name);
  }

  getObjectLinkPreset(link: TaskObjectLink): TagPreset {
    const evaKey = OBJECT_TYPE_TO_EVA[link.object_type?.toUpperCase()] ?? link.object_type;
    const base = EVA_OBJECT_TAGS[evaKey] ?? { label: link.object_type, severity: "secondary" as const };
    return { ...base, label: link.object_name || base.label };
  }

  /** Fiche de l'objet lié, pour ouvrir l'offre / le chantier depuis le badge */
  getObjectLinkRoute(link: TaskObjectLink): string | null {
    const base = OBJECT_LINK_ROUTES[link.object_type?.toUpperCase()];
    return base && link.object_id ? `${base}/${link.object_id}` : null;
  }

  // ==========================================================================
  // Interactions
  // ==========================================================================

  onCheckboxClick(event: Event): void {
    event.stopPropagation();
    // Toggle direct : non-terminée → DONE, DONE/CANCELLED → TODO
    const nextStatus = this.isDone() ? TaskStatus.TODO : TaskStatus.DONE;
    this.statusChange.emit({ task: this.task, done: nextStatus === TaskStatus.DONE, status: nextStatus });
  }

  onTitleClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.selectionMode || event.shiftKey) {
      this.selectionToggle.emit(this.task);
      return;
    }
    this.taskClick.emit(this.task);
  }

  onRowClick(event: MouseEvent): void {
    if (this.selectionMode || event.shiftKey) {
      event.stopPropagation();
      this.selectionToggle.emit(this.task);
    }
  }

  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuRequested.emit({ task: this.task, x: event.clientX, y: event.clientY });
  }

  onDocumentClick(event: MouseEvent): void {
    // Le bouton ⋮ et les entrées du menu gèrent eux-mêmes la fermeture
    const menu = this.host.nativeElement.querySelector(".task-row__menu");
    if (menu?.contains(event.target as Node)) return;
    if (this._ignoreNextDocumentClick) {
      this._ignoreNextDocumentClick = false;
      return;
    }
    if (this.showRowMenu) {
      this.showRowMenu = false;
      this.cdr.markForCheck();
    }
  }

  toggleRowMenu(event: Event): void {
    event.stopPropagation();
    // Dans une liste, le ⋮ ouvre le menu du clic droit (mêmes options), aligné sous le bouton
    if (this.contextMenuRequested.observed) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.contextMenuRequested.emit({ task: this.task, x: rect.right, y: rect.bottom + 4, alignRight: true });
      return;
    }
    if (!this.showRowMenu) {
      // Fermer tous les autres menus ouverts
      this._ignoreNextDocumentClick = true;
      document.body.dispatchEvent(new MouseEvent("click", { bubbles: true }));
      this._ignoreNextDocumentClick = false;

      const btn = event.currentTarget as HTMLElement;
      const rect = btn.getBoundingClientRect();
      const MENU_HEIGHT = 90;
      this.menuOpensUpward = window.innerHeight - rect.bottom < MENU_HEIGHT + 8;
      this.menuPosition = {
        top: rect.bottom + 4,
        bottom: window.innerHeight - rect.top + 4,
        right: window.innerWidth - rect.right,
      };
      this.menuPlaced = false;
      this.placeMenuUnder(btn, rect);
    }
    this.showRowMenu = !this.showRowMenu;
    this.cdr.markForCheck();
  }

  /**
   * Dans un panneau animé (transform, ex. galet), position: fixed part du panneau et non de
   * l'écran : on mesure le menu rendu et on corrige l'écart pour le coller au bouton.
   */
  private placeMenuUnder(btn: HTMLElement, rect: DOMRect): void {
    setTimeout(() => {
      const menu = btn.parentElement?.querySelector<HTMLElement>(".task-row__menu-dropdown");
      if (!menu) return;
      const placed = menu.getBoundingClientRect();
      if (this.menuOpensUpward) {
        this.menuPosition.bottom += placed.bottom - (rect.top - 4);
      } else {
        this.menuPosition.top += rect.bottom + 4 - placed.top;
      }
      this.menuPosition.right += placed.right - rect.right;
      this.menuPlaced = true;
      this.cdr.markForCheck();
    });
  }

  onMenuAction(action: string, event: Event): void {
    event.stopPropagation();
    this.showRowMenu = false;
    this.menuAction.emit({ task: this.task, action });
    this.cdr.markForCheck();
  }

  openDetail(event: Event): void {
    event.stopPropagation();
    this.detailRequested.emit(this.task);
  }

  // ==========================================================================
  // Affichage assignees (max 2 + surplus)
  // ==========================================================================

  getVisibleAssignees(): TaskAssignee[] {
    return this.task?.assigned_to || [];
  }
}
